import type { CssLength, HexColor } from './registry';

// The button half of the theme contract, split out of `registry.ts` so that file stays under its
// line budget. Its twin is `ThemeButtonSchema` in `backend/packages/shared/src/schemas/theme.schema.ts`;
// the two are edited together. A theme paints exactly two buttons and every call to action wears one,
// so recolouring a shop's buttons is one edit here rather than one per call to action.

/** One of the two buttons a theme paints; the paint itself lives in `ThemeStyles.buttons`. */
export type ButtonVariant = 'primary' | 'secondary';

/** The shape a button is drawn in — filled, outlined or bare text. Shape is not colour. */
export type ButtonShape = 'solid' | 'outline' | 'text';

/**
 * One button's whole skin. `gradientFrom` is the second stop a theme that fills its buttons with a
 * gradient reads; a flat theme repeats `background` there and the ramp disappears.
 *
 * Paint and geometry both, because a skin that names only the paint leaves the size to whichever
 * stylesheet reaches the button first — which is how six themes ended up with six font weights and
 * four border widths that no token could reach. The twin is `ThemeButtonSchema`; edit the two together.
 */
export type ThemeButton = {
  background: HexColor;
  gradientFrom: HexColor;
  textColor: HexColor;
  hoverBackground: HexColor;
  borderColor: HexColor;
  radius: CssLength;
  /** The space above and below the label, and to either side of it. */
  paddingBlock: CssLength;
  paddingInline: CssLength;
  fontSize: CssLength;
  /** 100–900, the same range the theme's own `headingWeight` is spelled in. */
  fontWeight: number;
  /** Nought for a button that draws no edge; the outline shape reads it too. */
  borderWidth: CssLength;
};

/** The two buttons a theme paints; every call to action on every page wears one of them. */
export type ThemeButtons = { primary: ThemeButton; secondary: ThemeButton };


/**
 * The geometry a button falls back to when its theme names none.
 *
 * Derived from the theme's own scales rather than written as literals, so a theme that retunes its
 * spacing or its base size retunes its buttons with it. `1px` is the one number here that is not a
 * token, because no theme ships a border scale to take it from — the day one does, this reads it.
 *
 * Three copies of this fallback existed before it was hoisted: `resolveStyleToken.paletteButton` and a
 * private `paletteButtons` inside souk. They agreed on the paint and said nothing at
 * all about the size, which is the whole reason six themes drifted apart.
 */
// Generic over how the caller spells a length: the storefront's own types narrow it to `CssLength`, while
// the same values arriving off the wire are plain `string`. One helper serves both rather than a cast.
export const buttonGeometry = <L extends string>(
  fonts: { baseSize: L },
  spacing: { xs: L; sm: L }
): { paddingBlock: L; paddingInline: L; fontSize: L; fontWeight: number; borderWidth: '1px' } => ({
  paddingBlock: spacing.xs,
  paddingInline: spacing.sm,
  fontSize: fonts.baseSize,
  fontWeight: 600,
  borderWidth: '1px'
});
